/**
 * useIdleLogout — kiosk-only idle-timer watchdog.
 *
 * Listens for user-input events. If none fire for `timeoutMs`, signals
 * idle. A consumer (IdleWatchdog component) decides what to do — show
 * a "you'll be logged out in 30s" warning, then actually log out.
 *
 * On companion form factor, this hook is a no-op (timeoutMs=0 from
 * useFormFactor → never fires).
 */

import { useEffect, useRef, useState } from 'react';

const INPUT_EVENTS = [
  'mousedown',
  'mousemove',
  'keydown',
  'touchstart',
  'wheel',
  'pointerdown',
] as const;

export interface UseIdleLogoutOpts {
  timeoutMs: number;
  /** Show warning UI this many ms before the actual logout. */
  warnAheadMs?: number;
  /** Called when warning window enters. */
  onWarn?: () => void;
  /** Called when timeout fires. */
  onTimeout: () => void;
}

export function useIdleLogout({
  timeoutMs,
  warnAheadMs = 30_000,
  onWarn,
  onTimeout,
}: UseIdleLogoutOpts): { remainingMs: number; reset: () => void; isWarning: boolean } {
  const [remainingMs, setRemainingMs] = useState(timeoutMs);
  const [isWarning, setIsWarning] = useState(false);
  const lastActivityRef = useRef(Date.now());

  useEffect(() => {
    if (timeoutMs <= 0) return; // disabled (companion mode)

    function bump() {
      lastActivityRef.current = Date.now();
      setIsWarning(false);
    }

    INPUT_EVENTS.forEach((ev) => {
      window.addEventListener(ev, bump, { passive: true });
    });

    const interval = window.setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      const remaining = Math.max(0, timeoutMs - elapsed);
      setRemainingMs(remaining);

      if (remaining <= warnAheadMs && remaining > 0 && !isWarning) {
        setIsWarning(true);
        onWarn?.();
      }
      if (remaining === 0) {
        onTimeout();
      }
    }, 1000);

    return () => {
      INPUT_EVENTS.forEach((ev) => window.removeEventListener(ev, bump));
      window.clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeoutMs, warnAheadMs]);

  function reset() {
    lastActivityRef.current = Date.now();
    setIsWarning(false);
    setRemainingMs(timeoutMs);
  }

  return { remainingMs, reset, isWarning };
}
